import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Input, Select, Switch, InputNumber, Button, Space, Table, Tag, Modal, message, Typography, Collapse } from 'antd';
import { PlusOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import request from '../../../utils/request';

const { Option } = Select;
const { Title } = Typography;

interface AuditModule {
  id?: number;
  key: string;
  name: string;
  enabled: boolean;
  default_severity: 'info' | 'warn' | 'error';
  default_retention_days: number;
  meta?: any;
}

interface AuditEventSpec {
  id?: number;
  module: number; // module id
  module_key?: string;
  event_type: string;
  enabled: boolean;
  min_severity: 'info' | 'warn' | 'error';
  schema?: any;
  mask_fields: string[];
  retention_days?: number | null;
  description?: string;
}

const severityColor = (s?: string) => (s === 'error' ? 'red' : s === 'warn' ? 'orange' : 'blue');

const InstructionLogConfigPage: React.FC = () => {
  const [moduleForm] = Form.useForm();
  const [specForm] = Form.useForm();
  const [newModuleForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState<AuditModule[]>([]);
  const [moduleData, setModuleData] = useState<AuditModule | null>(null);
  const [specs, setSpecs] = useState<AuditEventSpec[]>([]);

  const loadAll = async (keepSelected = true) => {
    setLoading(true);
    try {
      const resp = await request.get('/api/v1/audit/modules/');
      const list: AuditModule[] = Array.isArray(resp?.data?.results) ? resp.data.results : (resp?.data || []);
      setModules(list);
      // 默认不选择模块：需点击“管理”后才显示配置面板
      setModuleData(null);
      setSpecs([]);
    } catch (e) {
      message.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveModule = async () => {
    if (!moduleData) return;
    try {
      setSaving(true);
      const values = moduleForm.getFieldsValue();
      await request.patch(`/api/v1/audit/modules/${moduleData.id}/`, {
        name: values.name,
        enabled: values.enabled,
        default_severity: values.default_severity,
        default_retention_days: values.default_retention_days,
      });
      message.success('模块配置已保存');
      loadAll();
    } catch (e) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const openCreateModule = () => {
    newModuleForm.resetFields();
    newModuleForm.setFieldsValue({ enabled: true, default_severity: 'info', default_retention_days: 365 });
    Modal.confirm({
      title: '新增模块',
      icon: null,
      okText: '创建',
      cancelText: '取消',
      width: 600,
      content: (
        <Form form={newModuleForm} layout="vertical">
          <Form.Item name="key" label="模块标识" rules={[{ required: true, pattern: /^[a-z][a-z0-9_\-]*$/, message: '仅小写字母、数字、-、_' }]}>
            <Input placeholder="instruction / billing / auth" />
          </Form.Item>
          <Form.Item name="name" label="模块名称" rules={[{ required: true }]}>
            <Input placeholder="指令 / 账单 / 认证" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="enabled" label="启用" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="default_severity" label="默认级别" rules={[{ required: true }]}>
                <Select>
                  <Option value="info">info</Option>
                  <Option value="warn">warn</Option>
                  <Option value="error">error</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="default_retention_days" label="默认保留(天)" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ),
      onOk: async () => {
        try {
          const v = await newModuleForm.validateFields();
          const resp = await request.post('/api/v1/audit/modules/', v);
          message.success('已创建模块');
          await loadAll(false);
          setModuleData(resp?.data as AuditModule);
        } catch (e:any) {
          if (e?.errorFields) return Promise.reject();
          message.error('创建失败');
          return Promise.reject();
        }
      }
    });
  };

  const openCreateSpec = () => {
    if (!moduleData) return;
    specForm.resetFields();
    specForm.setFieldsValue({ enabled: true, min_severity: 'info', mask_fields: [] });
    Modal.confirm({
      title: '新建事件规格',
      icon: null,
      okText: '保存',
      cancelText: '取消',
      width: 680,
      content: (
        <SpecForm form={specForm} />
      ),
      onOk: async () => {
        try {
          const v = await specForm.validateFields();
          await request.post('/api/v1/audit/event-specs/', {
            module: moduleData.id,
            event_type: v.event_type,
            enabled: v.enabled,
            min_severity: v.min_severity,
            mask_fields: v.mask_fields || [],
            retention_days: v.retention_days ?? null,
            description: v.description || '',
          });
          message.success('已创建');
          loadAll();
        } catch (e:any) {
          if (e?.errorFields) return Promise.reject();
          message.error('创建失败');
          return Promise.reject();
        }
      },
    });
  };

  const openEditSpec = (row: AuditEventSpec) => {
    specForm.setFieldsValue({
      event_type: row.event_type,
      enabled: row.enabled,
      min_severity: row.min_severity,
      mask_fields: row.mask_fields || [],
      retention_days: row.retention_days,
      description: row.description,
    });
    Modal.confirm({
      title: `编辑事件规格 - ${row.event_type}`,
      icon: null,
      okText: '保存',
      cancelText: '取消',
      width: 680,
      content: (
        <SpecForm form={specForm} disableEventType />
      ),
      onOk: async () => {
        try {
          const v = await specForm.validateFields();
          await request.patch(`/api/v1/audit/event-specs/${row.id}/`, {
            enabled: v.enabled,
            min_severity: v.min_severity,
            mask_fields: v.mask_fields || [],
            retention_days: v.retention_days ?? null,
            description: v.description || '',
          });
          message.success('已保存');
          loadAll();
        } catch (e:any) {
          if (e?.errorFields) return Promise.reject();
          message.error('保存失败');
          return Promise.reject();
        }
      },
    });
  };

  const moduleColumns = useMemo(() => [
    { title: '标识', dataIndex: 'key', key: 'key', width: 140 },
    { title: '名称', dataIndex: 'name', key: 'name' },
    { title: '启用', dataIndex: 'enabled', key: 'enabled', width: 90, render: (v:boolean) => v ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
    { title: '默认级别', dataIndex: 'default_severity', key: 'default_severity', width: 110, render: (v:string) => <Tag color={severityColor(v)}>{v}</Tag> },
    { title: '默认保留(天)', dataIndex: 'default_retention_days', key: 'default_retention_days', width: 140 },
    { title: '操作', key: 'ops', width: 100, render: (_:any, r:AuditModule) => (
      <Space size={4}>
        <Button size="small" type="link" onClick={async () => {
          setModuleData(r);
          moduleForm.setFieldsValue({ name: r.name, enabled: r.enabled, default_severity: r.default_severity, default_retention_days: r.default_retention_days });
          const resp2 = await request.get('/api/v1/audit/event-specs/', { params: { module: r.key } });
          const evts: AuditEventSpec[] = Array.isArray(resp2?.data?.results) ? resp2.data.results : (resp2?.data || []);
          setSpecs(evts);
        }}>管理</Button>
      </Space>
    )}
  ], [moduleForm]);

  const columns = useMemo(() => [
    { title: '事件', dataIndex: 'event_type', key: 'event_type' },
    { title: '启用', dataIndex: 'enabled', key: 'enabled', width: 80, render: (v:boolean) => v ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
    { title: '最低级别', dataIndex: 'min_severity', key: 'min_severity', width: 110, render: (v:string) => <Tag color={severityColor(v)}>{v}</Tag> },
    { title: '脱敏字段', dataIndex: 'mask_fields', key: 'mask_fields', render: (arr:string[]) => (Array.isArray(arr) && arr.length>0 ? arr.map((f, i) => <Tag key={i}>{f}</Tag>) : '-') },
    { title: '保留期(天)', dataIndex: 'retention_days', key: 'retention_days', width: 120, render: (v:any) => v ?? '-' },
    { title: '说明', dataIndex: 'description', key: 'description' },
    { title: '操作', key: 'ops', width: 100, fixed: 'right' as const, render: (_:any, r:AuditEventSpec) => (
      <Space size={4}>
        <Button size="small" type="link" onClick={() => openEditSpec(r)}>编辑</Button>
      </Space>
    )},
  ], []);

  return (
    <div className="list-page-container">
      <Card>
        {/* 顶部标题行移除，统一由 TabsManager 控制标签标题与浏览器标题 */}

        <Collapse style={{ marginBottom: 16 }}>
          <Collapse.Panel header="使用说明" key="help">
            <div style={{ color: '#555' }}>
              <div>1) 在上方“模块列表”中点击“新增模块”，填写：</div>
              <div style={{ paddingLeft: 16 }}>- 模块标识：小写字母/数字/“-”/“_”，如 instruction、billing</div>
              <div style={{ paddingLeft: 16 }}>- 模块名称：显示名称，如 指令、账单</div>
              <div style={{ paddingLeft: 16 }}>- 默认策略：启用、默认级别、默认保留天数</div>
              <div>2) 创建后点击该模块行的“管理”，在“模块配置”中保存启用与默认策略。</div>
              <div>3) 在“事件规格”中新建所需事件（如 instruction.created / instruction.status_changed / instruction.cancelled）：</div>
              <div style={{ paddingLeft: 16 }}>- 启用：是否记录该事件</div>
              <div style={{ paddingLeft: 16 }}>- 最低级别：低于该级别的事件不记录（info/warn/error）</div>
              <div style={{ paddingLeft: 16 }}>- 脱敏字段：点号路径，多个以逗号分隔，如 customer.phone, answers.0.value</div>
              <div style={{ paddingLeft: 16 }}>- 保留(天)：如不填则继承模块默认</div>
              <div>4) 业务侧在关键节点调用统一日志函数（后端）：log_event(event_type, target, context...) 按以上配置自动落库。</div>
              <div>5) 配置可随时调整，保存后立即生效；“日志列表”页可按模块/事件/对象检索查看。</div>
            </div>
          </Collapse.Panel>
        </Collapse>

        <Card size="small" title="模块列表" style={{ marginBottom: 16 }} extra={<Button icon={<PlusOutlined />} onClick={openCreateModule}>新增模块</Button>}>
          <Table
            loading={loading}
            rowKey={(r) => String((r as any).id)}
            dataSource={modules}
            columns={moduleColumns}
            pagination={false}
            size="small"
          />
        </Card>

        {moduleData && (
        <Card size="small" title={`模块配置 （${moduleData.key}）`} style={{ marginBottom: 16 }}>
          <Form form={moduleForm} layout="horizontal">
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="模块标识">
                  <Input value={moduleData?.key || '-'} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="name" label="模块名称" rules={[{ required: true }]}>
                  <Input placeholder="模块名称" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="enabled" label="是否启用" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="default_severity" label="默认级别" rules={[{ required: true }]}>
                  <Select>
                    <Option value="info">info</Option>
                    <Option value="warn">warn</Option>
                    <Option value="error">error</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="default_retention_days" label="默认保留(天)" rules={[{ required: true }]}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Space>
                  <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveModule} disabled={!moduleData}>保存</Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>
        )}

        {moduleData && (
        <Card size="small" title={`事件规格 （${moduleData.key}）`} extra={<Button icon={<PlusOutlined />} onClick={openCreateSpec}>新建事件规格</Button>}>
          <Table
            loading={loading}
            rowKey={(r) => String((r as any).id)}
            dataSource={specs}
            columns={columns}
            pagination={false}
            scroll={{ x: 1000 }}
          />
          {(!specs || specs.length === 0) && (
            <div style={{ padding: 12, color: '#888' }}>暂无事件规格，点击右上角“新建事件规格”添加，如 instruction.created、instruction.status_changed、instruction.cancelled、instruction.result_submitted 等。</div>
          )}
        </Card>
        )}
      </Card>
    </div>
  );
};

const SpecForm: React.FC<{ form: any; disableEventType?: boolean; }> = ({ form, disableEventType }) => {
  return (
    <Form form={form} layout="vertical">
      <Form.Item name="event_type" label="事件标识" rules={[{ required: true }]}>
        <Input placeholder="instruction.status_changed" disabled={disableEventType} />
      </Form.Item>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="min_severity" label="最低级别" rules={[{ required: true }]}>
            <Select>
              <Option value="info">info</Option>
              <Option value="warn">warn</Option>
              <Option value="error">error</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="retention_days" label="保留(天)">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="mask_fields" label="脱敏字段(点号路径，逗号分隔)">
        <Input placeholder="如 customer.phone, answers.0.value" onChange={(e) => {
          const raw = e.target.value as string;
          const arr = raw.split(',').map(s => s.trim()).filter(Boolean);
          form.setFieldsValue({ mask_fields: arr });
        }} />
      </Form.Item>
      <Form.Item name="description" label="说明">
        <Input.TextArea rows={3} placeholder="此事件记录的业务含义与字段" />
      </Form.Item>
    </Form>
  );
};

export default InstructionLogConfigPage;


